import { fg } from '@atlassian/jira-feature-gating';
import {
	CustomFieldsTooltip,
	type CustomFieldsTooltipProps,
} from '../ui/metric-detail/metric-view/custom-field-optimiser/tooltip/index.tsx';
import { CUSTOM_FIELDS_KEY } from './assets/custom-fields/index.tsx';
import { ISSUE_KEY } from './assets/issue/index.tsx';
import { PROJECT_CLEANUP_KEY } from './assets/project-cleanup/index.tsx';
import { PROJECT_ROLE_ACTOR_KEY } from './assets/project-role-actor/index.tsx';
import type { Metric } from './assets/types.tsx';
import { METRICS } from './constants.tsx';

/**
 * A registry of all the metrics. It provides the methods for looking up the metrics that are registered.
 * *Exported for testing purposes only. DO NOT used this class directly.*
 */
export class MetricRegistry {
	private metrics: ReadonlyArray<Metric>;

	public constructor(metrics: ReadonlyArray<Metric>) {
		this.metrics = metrics;
	}

	public getMetric(key: string): Metric | undefined {
		return this.metrics.find((m) => m.key === key);
	}

	public getAllVisibleMetrics(): Metric[] {
		return this.metrics.filter((metric) => metric.visible && mitigationEnabled(metric.key));
	}

	public getAllVisibleMetricsKeys(): string[] {
		return getAllVisibleMetrics().map((m) => m.key);
	}
}

/**
 * Metric registry singleton. We should not be creating another registry.
 */
const REGISTRY = new MetricRegistry(METRICS);

/**
 * Get metric using the metric key
 * @param key
 */
export const getMetric = REGISTRY.getMetric.bind(REGISTRY);

/**
 * Get all metrics that are visible
 */
export const getAllVisibleMetrics = REGISTRY.getAllVisibleMetrics.bind(REGISTRY);

/**
 * Get all metric keys for visible metrics
 */
export const getAllVisibleMetricKeys = REGISTRY.getAllVisibleMetricsKeys.bind(REGISTRY);

const mitigationEnabled = (metricKey: string) => {
	switch (metricKey) {
		case PROJECT_CLEANUP_KEY:
			return fg('site-optimizer-project-cleanup');
		case PROJECT_ROLE_ACTOR_KEY:
		case CUSTOM_FIELDS_KEY:
		case ISSUE_KEY:
			return true;
		default:
			return false;
	}
};

export const assertNever = (_: never, message: string) => {
	throw new Error(message);
};

export const TooltipMap: TooltipMap = {
	[CUSTOM_FIELDS_KEY]: CustomFieldsTooltip,
};

type TooltipProps = CustomFieldsTooltipProps;

type TooltipMap = {
	[key: string]: ({ children, dataRef, metricState }: TooltipProps) => React.JSX.Element | null;
};
